import React, { useEffect, useState } from 'react';
import { ClipboardCheck, Inbox } from 'lucide-react';
import { useHistory, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../../../components/ui/card';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import ApiService from '../../../../services/api.service';
import ReviewsTable from './components/ReviewsTable';

function ReviewsPageV3() {
  const history = useHistory();
  const { department } = useParams();

  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    ApiService.head.getDepartmentReviews(department).then((data) => {
      setReviews(data);
      setIsLoading(false);
    });
  }, [department]);

  const handleReviewClick = (review) => {
    history.push(`${history.location.pathname}/${review._id}?user=${review.user.email}`);
  };

  return (
    <ScrollArea className="h-full">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <ClipboardCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Reviews</h1>
              <p className="text-muted-foreground text-sm">
                Review pending level-up requests for {department} department
              </p>
            </div>
          </div>
          {!isLoading && reviews.length > 0 && (
            <div className="px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-600 text-sm font-medium">
              {reviews.length} pending {reviews.length === 1 ? 'review' : 'reviews'}
            </div>
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <Card style={{ border: '1px solid hsl(var(--border))' }}>
            <CardContent className="p-0">
              <div className="space-y-0">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4 p-4 border-b"
                    style={{ borderColor: 'hsl(var(--border))' }}
                  >
                    <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-48 bg-muted animate-pulse rounded" />
                      <div className="h-3 w-32 bg-muted animate-pulse rounded" />
                    </div>
                    <div className="h-6 w-16 bg-muted animate-pulse rounded-full" />
                    <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                    <div className="h-9 w-24 bg-muted animate-pulse rounded" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!isLoading && reviews.length === 0 && (
          <Card style={{ border: '1px solid hsl(var(--border))' }}>
            <CardContent className="py-16">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="p-4 rounded-full bg-muted mb-4">
                  <Inbox className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-1">No pending reviews</h3>
                <p className="text-sm text-muted-foreground max-w-md">
                  There are no review requests at the moment. When team members request a level-up
                  review, they will appear here.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reviews Table */}
        {!isLoading && reviews.length > 0 && (
          <ReviewsTable reviews={reviews} onReview={handleReviewClick} />
        )}
      </div>
    </ScrollArea>
  );
}

export default ReviewsPageV3;
